// Where a body's surface is, and whether a step of flight met it. The registry's
// radius_m on a body drawn as a sphere, and the DRAWN surface under the contact
// direction on one drawn as its own shape, so the trajectory's far end, the
// pilot panel's HEIGHT and this agree with the ground the player can see.

import type { FlightPull, FlightVec } from './flight.ts';
import { poleAndMeridianAt, type PoleAndMeridian } from '../world/orientation.ts';
import type { BodyRow } from '../world/types.ts';

/**
 * Every slot's centre `tauS` simulated seconds into the frame, into `into`. A
 * slot with no grid laid keeps the centre `pulls` already carries.
 *
 * `parentIdx` is the slot the flight frame is flown in, and the answers come
 * back SHIFTED onto where `pulls` holds that parent rather than onto the
 * parent's own place at `tauS`. The evaluation point a step of flight is taken
 * at is built on the held parent, so this is the one convention the two can be
 * differenced in; it also leaves the parent's own centre exactly the held one,
 * which is the cancellation that makes the frame's own surface exact.
 */
export type CentreReader = (
  tauS: number, into: readonly FlightVec[], pulls: readonly FlightPull[],
  parentIdx: number,
) => void;

/**
 * Where the drawn surface of slot `i` stands along one direction out of its
 * centre, metres. 0 is no answer, which leaves that slot on `radiiM`.
 *
 * The direction is in the same axes the segment arrives in and need not be a
 * unit vector. A reader that has to fetch or ray anything is asked only for a
 * slot whose `outerRadiiM` stands past its `radiiM`, so a body drawn as its
 * datum sphere costs nothing.
 *
 * `leadS` is how far past now, seconds, this direction is being asked for - 0
 * for a read of where things stand this instant, more for one the entry
 * bisection is trying deeper into the segment. A reader built on a live mesh's
 * CURRENT spin turns the direction back by the body's own rate over `leadS`
 * before it samples, so a contact minutes out reads the patch of ground that
 * will be under the ship then and not the one under it now; a reader with
 * nothing time-varying to turn may ignore it.
 */
export type SurfaceReader = (
  i: number, dx: number, dy: number, dz: number, leadS: number,
) => number;

/**
 * Turn a direction held against slot `i` forward by that body's own spin over
 * `dtS` seconds, in place, in the axes the segment arrives in. A slot with
 * nothing to turn by leaves it where it is.
 */
export type SpinTurner = (i: number, d: FlightVec, dtS: number) => void;

/**
 * Slot `i`'s own spin, as an angular velocity vector radians a second, into
 * `out`, in the same axes `spinBy` turns in: the pole direction times the
 * rate. Zero for a slot with nothing to turn by. Liftoff's tangential
 * velocity and a resting ship's own turning orientation both need this one
 * vector rather than two separate rotations of it.
 */
export type SpinRateReader = (i: number, out: FlightVec) => void;

/** What a step met, if it met anything. Cleared by the caller, never here. */
export interface FlightContact {
  hit: boolean;
  /** The row met, or the empty string. */
  bodyId: string;
  /** Speed relative to that body at the instant of the hit, m/s. */
  speedMps: number;
  /** Simulated seconds into the frame's flight step at which it was met. */
  atS: number;
}

/**
 * The surfaces one flight step can meet: one entry per `pulls` slot, in the
 * same order, so a caller that already holds those arrays hands them over
 * rather than building a set of its own.
 */
export interface FlightGround {
  /** Datum radius per slot, metres. 0 is a slot with no ground to meet. */
  readonly radiiM: readonly number[];
  /**
   * Where the DRAWN surface stands under a direction, per slot, or null for a
   * caller with no shape to read - the drawn path, a pinned registry - which
   * leaves every slot on its datum sphere.
   */
  readonly surfaceAt: SurfaceReader | null;
  /**
   * Turns a resting ship's offset with the body under it, so the seat stays on
   * the patch of ground the contact met while the body spins. Null holds the
   * offset still in the flight frame's axes.
   */
  readonly spinBy: SpinTurner | null;
  /**
   * Slot `i`'s own spin as an angular velocity vector, radians a second, the
   * same pole and rate `spinBy` turns the offset by. Null is nothing to spin
   * by, same as a null `spinBy`.
   */
  readonly spinRateAt: SpinRateReader | null;
  /**
   * Per slot, a radius no part of the drawn body reaches past, metres. A slot
   * whose bound is its own `radiiM` is a sphere and never reads a surface;
   * null is every slot a sphere. Phobos's shape runs 2 km either side of its
   * 11,080 m datum, so the bound is what the sweep has to miss before it can
   * rule the body out.
   */
  readonly outerRadiiM: readonly number[] | null;
  /** The row behind each slot, so a contact can name what it landed on. */
  readonly ids: readonly string[];
  /**
   * Each slot's velocity in the frame the flight state is integrated in, so a
   * contact can set the ship's own velocity to the body's and the ship rides
   * it from there. Null is every body at rest in that frame, which is what the
   * frame's own parent is by construction.
   */
  readonly velIn: readonly FlightVec[] | null;
  /** Written by `stepFlight` when a substep's segment meets a surface. */
  readonly contact: FlightContact;
  /**
   * Where every centre stands at an arbitrary instant of the frame, set by
   * `stepFlightSpans`. Null is every centre held where `pulls` puts it, which
   * is what a caller with no grid laid - the drawn path, a pinned registry -
   * can offer, and what the sweep read before there was a grid to ask.
   */
  centreAt: CentreReader | null;
  /** Simulated seconds into the frame at which the current `stepFlight` begins. */
  tau0: number;
  /** The slot the frame is flown in, which `centreAt` shifts its answers onto. */
  centreParent: number;
}

export function createContact(): FlightContact {
  return { hit: false, bodyId: '', speedMps: 0, atS: 0 };
}

/** Which slot a segment met, and how far along it. `idx` of -1 is no contact. */
export interface GroundHit {
  idx: number;
  f: number;
  /** The radius the segment was swept against, so the stop lands on it. */
  radiusM: number;
}

export function createGroundHit(): GroundHit {
  return { idx: -1, f: 1, radiusM: 0 };
}

/**
 * Where a segment first meets a sphere of `radiusM` about the ORIGIN, as a
 * fraction along it, or -1 for a segment that never does. A segment whose near
 * end is already inside reads 0.
 *
 * The segment arrives already in the body's own frame, which is what lets the
 * body move under it: the caller differences each end against the centre at
 * THAT end's own instant, so a sphere travelling across the substep is swept
 * rather than stood still at one sample of it.
 *
 * Swept rather than a point test at the far end. A substep is at most
 * `FLIGHT_MAX_SUBSTEP_S` (2 s), over which a ship closing at 30 km/s covers
 * 60 km: against Phobos's 11 km radius an end-point test misses the body
 * outright, and against Mars it puts the ship 60 km under the surface before
 * anything notices. A whole frame at the top warp level is 8,640 simulated
 * seconds, which is why this is asked once per substep.
 */
export function sphereSweep(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  radiusM: number,
): number {
  if (!(radiusM > 0)) return -1;
  const mx = ax;
  const my = ay;
  const mz = az;
  const c = mx * mx + my * my + mz * mz - radiusM * radiusM;
  const dx = bx - ax;
  const dy = by - ay;
  const dz = bz - az;
  const a = dx * dx + dy * dy + dz * dz;
  if (!(a > 0)) return -1;
  const b = mx * dx + my * dy + mz * dz;
  // Starting on or inside the sphere: a contact only while it is still going
  // further in. A ship resting ON a surface sits at exactly the radius, and a
  // lift-off burn leaving from there must not read as a fresh landing.
  if (c <= 0) return b < 0 ? 0 : -1;
  // Leaving the sphere's neighbourhood, or running tangentially away from it.
  if (b >= 0) return -1;
  const disc = b * b - a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / a;
  return t >= 0 && t <= 1 ? t : -1;
}

/**
 * Where a slot's centre stands at one end of the substep being swept: the
 * epoch-accurate reading when the caller has one, and the slot's own otherwise.
 */
export function centreOf(
  pulls: readonly FlightPull[], centres: readonly FlightVec[] | null, i: number,
): FlightVec {
  const c = centres === null ? undefined : centres[i];
  return c ?? pulls[i]!.at;
}

/**
 * The drawn surface under the point of a body-relative segment that comes
 * nearest the centre, metres, or 0 where nothing answers.
 *
 * The nearest point rather than either end: on a SPHERE it is exactly the
 * point that sphere has to be right under, which is what makes it a sound
 * first guess for `enterDrawnSurface` below - but the drawn ground is not a
 * sphere, and the direction the segment actually crosses it along can stand
 * far from this one on a fast oblique pass.
 */
function nearestPointRadius(
  read: SurfaceReader, i: number,
  sx: number, sy: number, sz: number, ex: number, ey: number, ez: number,
  leadS: number, leadEndS: number,
): number {
  const dx = ex - sx;
  const dy = ey - sy;
  const dz = ez - sz;
  const dd = dx * dx + dy * dy + dz * dz;
  let t = 0;
  if (dd > 0) {
    t = -(sx * dx + sy * dy + sz * dz) / dd;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
  }
  const px = sx + dx * t;
  const py = sy + dy * t;
  const pz = sz + dz * t;
  const lead = leadS + (leadEndS - leadS) * t;
  // A segment through the centre itself names no direction; the near end does.
  if (px * px + py * py + pz * pz > 0) return read(i, px, py, pz, lead);
  return read(i, sx, sy, sz, lead);
}

const SURFACE_ENTRY_BISECT_STEPS = 24;
/**
 * The most chord samples `enterDrawnSurface` walks before it has a bracket to
 * bisect, so a crossing the two ends alone do not bracket - one that goes in
 * and comes back out within its own length - is still found: Phobos's 68 m
 * height-field cell over the longest chord this reads in one call, the
 * predicted line's own worst-case knot span (17.6 km at 3,689.5 m/s of
 * closing travel, `phobos-cross-line`'s reading), 17,600 / 68 rounded up.
 */
const SURFACE_ENTRY_SAMPLE_STEPS = 260;
/**
 * The walk's spacing, radians of the body's datum: one height-field cell, a
 * cube face's quarter turn over the 256 cells every shape body's level 0
 * carries, so a short chord pays for the cells it spans rather than for 260.
 */
const SURFACE_ENTRY_CELL_RAD = Math.PI / 2 / 256;
/** Scratch point for `enterDrawnSurface`'s own reads; never read outside it. */
const _entryAt = { x: 0, y: 0, z: 0 };

/**
 * Where a body-relative segment first meets the drawn surface `read` answers
 * for, as a fraction along it, with the radius it met into `out.radiusM` - or,
 * failing that, the `rNear`-based sphere sweep `surfaceSweep` already has in
 * hand, unchanged.
 *
 * `rNear` sampled one direction (the chord's nearest point to the centre) and
 * swept the whole segment against that one radius, which is exact only where
 * the surface happens to be a sphere. Bisecting instead reads the surface
 * again at each trial point, so the direction it finally answers on is the
 * direction the path actually crosses rather than the one first guessed at:
 * on Phobos this is most of what stood a fast oblique pass's stop up to
 * 2,150 m from where the drawn line said it was.
 *
 * A start already inside the drawn ground along its own direction is met
 * there, by the sphere's own rule against the ground under the start: a
 * contact while it still goes further in. From a start outside, the stretch
 * of the chord inside `outerM` is sampled forward a height-field cell apart,
 * at most `SURFACE_ENTRY_SAMPLE_STEPS` times, for the first sample that
 * stands at or inside the drawn ground - the segment's own far end need not
 * be the one that does, only whichever sample first finds it, which is what
 * lets a chord that enters and leaves again within its own length still be
 * caught. Nothing standing inside over the whole walk falls back to the
 * `rNear` sweep.
 * `legacyNearestPoint` skips straight to that sweep, which is
 * `ground-contact-check.mjs --inject nearest-point`'s own control.
 */
function enterDrawnSurface(
  read: SurfaceReader, i: number,
  sx: number, sy: number, sz: number, ex: number, ey: number, ez: number,
  lead0S: number, lead1S: number, rNear: number, datumM: number, outerM: number,
  out: GroundHit, legacyNearestPoint: boolean, sampleSteps: number,
): number {
  const legacyF = sphereSweep(sx, sy, sz, ex, ey, ez, rNear);
  if (legacyNearestPoint) {
    out.radiusM = rNear;
    return legacyF;
  }
  const dx = ex - sx;
  const dy = ey - sy;
  const dz = ez - sz;

  // The drawn radius at fraction `f` along the segment, `_entryAt` left on the
  // point it was read at so `excessAt` need not walk the chord a second time.
  function radiusAt(f: number): number {
    let px = sx + dx * f;
    let py = sy + dy * f;
    let pz = sz + dz * f;
    if (px === 0 && py === 0 && pz === 0) { px = sx; py = sy; pz = sz; }
    _entryAt.x = px;
    _entryAt.y = py;
    _entryAt.z = pz;
    return read(i, px, py, pz, lead0S + (lead1S - lead0S) * f);
  }

  // Positive outside the drawn ground along `point(f)`'s own direction, 0 on
  // it, negative inside - the one scalar the bisection below tracks the sign
  // of.
  function excessAt(f: number): number {
    const r = radiusAt(f);
    return _entryAt.x * _entryAt.x + _entryAt.y * _entryAt.y + _entryAt.z * _entryAt.z - r * r;
  }

  if (excessAt(0) < 0) {
    out.radiusM = radiusAt(0);
    return sphereSweep(sx, sy, sz, ex, ey, ez, out.radiusM);
  }
  // Only the stretch inside the bound can reach the ground, and the point it
  // comes in at stands outside the relief, so the walk starts there unread.
  const a = dx * dx + dy * dy + dz * dz;
  const b = sx * dx + sy * dy + sz * dz;
  const disc = b * b - a * (sx * sx + sy * sy + sz * sz - outerM * outerM);
  const f0 = disc > 0 ? Math.max(0, (-b - Math.sqrt(disc)) / a) : 0;
  const f1 = disc > 0 ? Math.min(1, (-b + Math.sqrt(disc)) / a) : 1;
  const n = sampleSteps < SURFACE_ENTRY_SAMPLE_STEPS ? sampleSteps : Math.max(1, Math.min(
    SURFACE_ENTRY_SAMPLE_STEPS,
    Math.ceil((Math.sqrt(a) * (f1 - f0)) / (datumM * SURFACE_ENTRY_CELL_RAD))));
  let loF = -1;
  let hiF = -1;
  let prevF = f0;
  for (let s = 1; s <= n && f1 > f0; s++) {
    const f = f0 + ((f1 - f0) * s) / n;
    if (excessAt(f) <= 0) { loF = prevF; hiF = f; break; }
    prevF = f;
  }
  if (loF < 0) {
    out.radiusM = rNear;
    return legacyF;
  }
  let lo = loF;
  let hi = hiF;
  for (let b = 0; b < SURFACE_ENTRY_BISECT_STEPS; b++) {
    const mid = (lo + hi) / 2;
    if (excessAt(mid) >= 0) lo = mid; else hi = mid;
  }
  out.radiusM = radiusAt(hi);
  return hi;
}

/**
 * Where a body-relative segment first meets slot `i`'s ground, as a fraction
 * along it, or -1; the radius it was swept against goes to `out.radiusM`.
 *
 * A slot whose `outerM` stands past its `datumM` is met in two passes when
 * there is a reader: the bound, which nothing of the body reaches past, and
 * then the entry along the direction the segment actually crosses the drawn
 * ground, bisected out of `enterDrawnSurface` above. Anything else sweeps
 * once against `datumM`. The flight's own contact and the predicted line's
 * far end both stop here (`src/sim/trajectory.ts`).
 *
 * `lead0S`/`lead1S` are how far the segment's own two ends stand ahead of
 * now, seconds, so a reader that turns its answer with the body's own spin
 * reads the ground at the instant the contact is actually near rather than at
 * whatever instant the caller happened to ask from. `legacyNearestPoint` and a
 * `sampleSteps` under `SURFACE_ENTRY_SAMPLE_STEPS` are `enterDrawnSurface`'s
 * own controls; nothing a player reaches sets either.
 */
export function surfaceSweep(
  read: SurfaceReader | null, i: number, datumM: number, outerM: number,
  sx: number, sy: number, sz: number, ex: number, ey: number, ez: number,
  lead0S: number, lead1S: number,
  out: GroundHit, legacyNearestPoint = false, sampleSteps = SURFACE_ENTRY_SAMPLE_STEPS,
): number {
  if (read !== null && outerM > datumM) {
    // Nothing of the body stands past the bound, so a segment that misses it
    // cannot meet the shape inside it and costs no read at all.
    if (sphereSweep(sx, sy, sz, ex, ey, ez, outerM) < 0) return -1;
    const rNear = nearestPointRadius(read, i, sx, sy, sz, ex, ey, ez, lead0S, lead1S);
    if (rNear > 0) {
      return enterDrawnSurface(
        read, i, sx, sy, sz, ex, ey, ez, lead0S, lead1S, rNear, datumM, outerM, out,
        legacyNearestPoint, sampleSteps);
    }
  }
  out.radiusM = datumM;
  return sphereSweep(sx, sy, sz, ex, ey, ez, datumM);
}

const _slotHit: GroundHit = createGroundHit();

/**
 * The first surface a segment meets across the whole pulling set, into `out`.
 * Every slot with a radius, not just the frame's own parent: a run at Phobos
 * is flown in Mars's frame the whole way in.
 *
 * `c0` and `c1` are every centre at the substep's own start and end, so each
 * body is swept in ITS frame rather than against one sample of where it was.
 * Each slot is met through `surfaceSweep`: its drawn shape where it has one,
 * `radiiM` where it is a sphere. `lead0S`/`lead1S` are how far the substep's
 * own two ends stand ahead of now, seconds, carried straight through to it.
 */
export function findGroundContact(
  pulls: readonly FlightPull[],
  ground: FlightGround,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  c0: readonly FlightVec[] | null,
  c1: readonly FlightVec[] | null,
  lead0S: number, lead1S: number,
  out: GroundHit,
): boolean {
  out.idx = -1;
  out.f = 1;
  out.radiusM = 0;
  const read = ground.surfaceAt;
  const n = Math.min(pulls.length, ground.radiiM.length);
  for (let i = 0; i < n; i++) {
    const p = pulls[i]!;
    // A slot carrying no mass is a slot nothing filled this frame, and its
    // centre is left over from whenever something last did.
    if (!(p.gm > 0)) continue;
    const datumM = ground.radiiM[i] ?? 0;
    if (!(datumM > 0)) continue;
    const s = centreOf(pulls, c0, i);
    const e = centreOf(pulls, c1, i);
    const sx = ax - s.x;
    const sy = ay - s.y;
    const sz = az - s.z;
    const ex = bx - e.x;
    const ey = by - e.y;
    const ez = bz - e.z;
    const outerM = (ground.outerRadiiM === null ? 0 : ground.outerRadiiM[i] ?? 0);
    const f = surfaceSweep(
      read, i, datumM, outerM, sx, sy, sz, ex, ey, ez, lead0S, lead1S, _slotHit);
    if (f < 0) continue;
    if (out.idx < 0 || f < out.f) {
      out.idx = i;
      out.f = f;
      out.radiusM = _slotHit.radiusM;
    }
  }
  return out.idx >= 0;
}

/** The slot a grounded state is resting on, or -1 when the set no longer holds it. */
export function groundSlot(ground: FlightGround | null, bodyId: string | null): number {
  if (ground === null || bodyId === null) return -1;
  for (let i = 0; i < ground.ids.length; i++) {
    if (ground.ids[i] === bodyId) return i;
  }
  return -1;
}

const SPIN_OBLIQUITY_DEG = 23.4392911;
const SPIN_DEG = Math.PI / 180;
const _spinAt: PoleAndMeridian = { raDeg: 0, decDeg: 0, wDeg: null };

/**
 * A body's own rotation pole at `jdTdb`, in the sim's ecliptic axes - the
 * pole `bodyFixedQuaternion` (`src/atlas/orrery.ts`) reads, taken off the same
 * `poleAndMeridianAt` rather than through that file, which pulls in the whole
 * render graph and is not importable under Node's own module resolution.
 *
 * Not in the Atlas's own axes, `eclipticToScene`'s (x, z, -y): the drawn
 * mesh's world matrix carries that quarter turn back out
 * (`atlasRoot.rotation.x`), so the mesh spins about this pole in the axes the
 * flight hands its reader. `atlasAxes` puts the swap back, which is
 * `ground-contact-check.mjs --inject atlas-axes-pole`'s control.
 */
function bodyPoleAt(row: BodyRow, jdTdb: number, atlasAxes = false): FlightVec | null {
  if (!poleAndMeridianAt(row, jdTdb, _spinAt)) return null;
  const ra = _spinAt.raDeg * SPIN_DEG;
  const dec = _spinAt.decDeg * SPIN_DEG;
  const ex = Math.cos(dec) * Math.cos(ra);
  const ey = Math.cos(dec) * Math.sin(ra);
  const ez = Math.sin(dec);
  const c = Math.cos(SPIN_OBLIQUITY_DEG * SPIN_DEG);
  const s = Math.sin(SPIN_OBLIQUITY_DEG * SPIN_DEG);
  const yEcl = ey * c + ez * s;
  const zEcl = -ey * s + ez * c;
  const x = ex;
  const y = atlasAxes ? zEcl : yEcl;
  const z = atlasAxes ? -yEcl : zEcl;
  const len = Math.hypot(x, y, z);
  return len > 0 ? { x: x / len, y: y / len, z: z / len } : null;
}

/**
 * `dir` turned back about a body's own pole by the spin it turns through over
 * `leadS`, into `out`: a query minutes ahead of now has to arrive already
 * turned back by that much to read the patch of ground that will be under it
 * at contact, not the one under it now - Phobos turns 0.78 deg/min, Psyche
 * about 1.4, so this is minutes of drift on a near pass. `src/main.ts` calls
 * this for the live game; `ground-contact-check.mjs`'s `phobos-shape-spin`
 * calls it with Phobos's own real row and epoch, so a reverted rotation, a
 * flipped sign or an axis other than the pole is read by the same function
 * the game ships rather than a harness replica of it.
 */
export function spinAheadDir(
  row: BodyRow, jdTdb: number, dx: number, dy: number, dz: number, leadS: number,
  out: { x: number; y: number; z: number }, atlasAxes = false,
): void {
  out.x = dx;
  out.y = dy;
  out.z = dz;
  const rateDegDay = row.rotation.W_rate_deg_day;
  if (leadS === 0 || rateDegDay === null) return;
  const pole = bodyPoleAt(row, jdTdb, atlasAxes);
  if (pole === null) return;
  const angle = -(rateDegDay * SPIN_DEG) * (leadS / 86400);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dot = dx * pole.x + dy * pole.y + dz * pole.z;
  const cx = pole.y * dz - pole.z * dy;
  const cy = pole.z * dx - pole.x * dz;
  const cz = pole.x * dy - pole.y * dx;
  out.x = dx * cos + cx * sin + pole.x * dot * (1 - cos);
  out.y = dy * cos + cy * sin + pole.y * dot * (1 - cos);
  out.z = dz * cos + cz * sin + pole.z * dot * (1 - cos);
}

/**
 * A body's own spin at `jdTdb` as an angular velocity vector, radians a
 * second, into `out`: the pole `spinAheadDir` turns about, scaled by its
 * rate. The derivative of `spinAheadDir`'s own rotation at `leadS = 0`, so a
 * liftoff's tangential velocity (`omega x offset`) and a resting ship's own
 * turning orientation both read the one vector `spinAheadDir` already turns
 * a direction by, rather than each carrying a second copy of the pole and
 * rate math.
 * Mean W rate: Luna's periodic W terms move the drawn meridian up to 0.85 deg/day off it, about 0.3 m/s at the equator.
 */
export function bodySpinRateAt(
  row: BodyRow, jdTdb: number, out: { x: number; y: number; z: number }, atlasAxes = false,
): void {
  out.x = 0;
  out.y = 0;
  out.z = 0;
  const rateDegDay = row.rotation.W_rate_deg_day;
  if (rateDegDay === null) return;
  const pole = bodyPoleAt(row, jdTdb, atlasAxes);
  if (pole === null) return;
  const omega = (rateDegDay * SPIN_DEG) / 86400;
  out.x = pole.x * omega;
  out.y = pole.y * omega;
  out.z = pole.z * omega;
}
